import { Box, Tab, Tabs, Typography, useTheme } from "@mui/material";

import React, { SyntheticEvent, useCallback, useMemo, useState } from "react";

import { Device } from "../../../../../../API";
import LiveViewRecordingsTab from "./LiveViewRecordingsTab";
import LiveViewTabComponent from "./LiveViewTab";
import LiveViewTimelapseTab from "./LiveViewTimelapseTab";
import ChipComponent from "../../../../../../common/components/chip/ChipComponent";
import { DetailsTableItem } from "../dialog/DetailsTable";
import LocationIcon from "../../../../../../common/components/icons/LocationIcon";
import HashIcon from "../../../../../../common/components/icons/HashIcon";
import { DeviceDetailsSection } from "../dialog/LiveViewDialog";

enum LiveViewTab {
  Live,
  Recordings,
  Timelapse,
}

interface DeviceLiveViewModalContentProps {
  device: Device;
  locationName: string;
}

const DeviceLiveViewModalContentComponent = ({
  device,
  locationName,
}: DeviceLiveViewModalContentProps): JSX.Element => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<LiveViewTab>(LiveViewTab.Live);

  const { id, nodeId, name } = device;

  const deviceIdClean = useMemo(() => {
    return id.replace(/^DE#/, "");
  }, [id]);

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newValue: LiveViewTab): void => {
      setActiveTab(newValue);
    },
    []
  );

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case LiveViewTab.Live:
        return <LiveViewTabComponent device={device} />;
      case LiveViewTab.Recordings:
        return <LiveViewRecordingsTab deviceId={id} nodeId={nodeId} />;
      case LiveViewTab.Timelapse:
        return <LiveViewTimelapseTab device={device} />;
      default:
        return null;
    }
  }, [activeTab, device, id, nodeId]);

  const deviceDetailsItems = useMemo<DetailsTableItem[]>(
    () => [
      {
        label: "Location",
        content: (
          <ChipComponent
            variant="chip"
            label={locationName}
            iconComponent={<LocationIcon />}
            textStyles={{ fontWeight: 500 }}
          />
        ),
      },
      {
        label: "Device ID",
        content: (
          <ChipComponent
            variant="chip"
            label={deviceIdClean}
            iconComponent={<HashIcon />}
            textStyles={{ fontWeight: 500 }}
          />
        ),
      },
    ],
    [name, locationName]
  );

  return (
    <Box
      sx={{
        padding: "16px 24px",
      }}
    >
      <Box>
        <Typography
          variant="h3"
          sx={{
            fontSize: "22px",
          }}
          fontWeight={500}
        >
          {name}
        </Typography>
      </Box>

      <Box sx={{ marginTop: "1em" }}>
        <DeviceDetailsSection items={deviceDetailsItems} />
      </Box>

      <Box
        sx={{
          borderTop: (theme): string => `1px solid ${theme.palette.divider}`,
          borderBottom: (theme): string => `1px solid ${theme.palette.divider}`,
          mt: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 40,
              color: theme.palette.text.secondary,
            },
            "& .Mui-selected": {
              color: theme.palette.text.primary,
            },
            "& .MuiTabs-indicator": {
              height: 2,
            },
          }}
        >
          <Tab label="Live View" />
          <Tab label="Recordings" />
          <Tab label="Timelapse" />
        </Tabs>
      </Box>

      {tabContent}
    </Box>
  );
};

const DeviceLiveViewModalContent = React.memo(
  DeviceLiveViewModalContentComponent
);

DeviceLiveViewModalContent.displayName = "DeviceLiveViewModalContent";

export default DeviceLiveViewModalContent;
